import React, { useState, useEffect } from 'react';
import { ProjectPlanningStep } from '../../types';
import { apiService } from '../../services/api';
import { CreatePlanningStepModal } from './CreatePlanningStepModal';
import { EditPlanningStepModal } from './EditPlanningStepModal';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';

interface PlanningStepsListProps {
  projectId: string;
}

export const PlanningStepsList: React.FC<PlanningStepsListProps> = ({ projectId }) => {
  const [planningSteps, setPlanningSteps] = useState<ProjectPlanningStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState<ProjectPlanningStep | null>(null);

  useEffect(() => {
    fetchPlanningSteps();
  }, [projectId]);

  const fetchPlanningSteps = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPlanningSteps(projectId);
      setPlanningSteps(response.planningSteps);
      setError(null);
    } catch (error) {
      console.error('Error fetching planning steps:', error);
      setError('Fehler beim Laden der Planungsschritte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStep = async (stepData: {
    stepName: string;
    stepOrder: number;
    description?: string;
    isRequired?: boolean;
    stepData?: any;
    notes?: string;
  }) => {
    try {
      await apiService.createPlanningStep({
        projectId,
        ...stepData
      });
      await fetchPlanningSteps();
      setCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating planning step:', error);
      setError('Fehler beim Erstellen des Planungsschritts');
    }
  };

  const handleUpdateStep = async (stepId: string, updateData: {
    stepName?: string;
    stepOrder?: number;
    description?: string;
    isRequired?: boolean;
    isCompleted?: boolean;
    stepData?: any;
    notes?: string;
  }) => {
    try {
      await apiService.updatePlanningStep(stepId, updateData);
      await fetchPlanningSteps();
      setEditModalOpen(false);
      setSelectedStep(null);
    } catch (error) {
      console.error('Error updating planning step:', error);
      setError('Fehler beim Aktualisieren des Planungsschritts');
    }
  };

  const handleToggleCompletion = async (stepId: string) => {
    try {
      await apiService.togglePlanningStepCompletion(stepId);
      await fetchPlanningSteps();
    } catch (error) {
      console.error('Error toggling step completion:', error);
      setError('Fehler beim Ändern des Abschlussstatus');
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Planungsschritt löschen möchten?')) {
      return;
    }

    try {
      await apiService.deletePlanningStep(stepId);
      await fetchPlanningSteps();
    } catch (error) {
      console.error('Error deleting planning step:', error);
      setError('Fehler beim Löschen des Planungsschritts');
    }
  };

  const handleEditStep = (step: ProjectPlanningStep) => {
    setSelectedStep(step);
    setEditModalOpen(true);
  };

  // Calculate progress
  const completedSteps = planningSteps.filter(step => step.isCompleted).length;
  const progressPercentage = planningSteps.length > 0 ? (completedSteps / planningSteps.length) * 100 : 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Planungsschritte</h3>
          <p className="text-sm text-gray-600">
            Strukturierter Workflow für die Projektplanung
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PlusIcon className="w-4 h-4 mr-2" />
          Schritt hinzufügen
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Progress Bar */}
      {planningSteps.length > 0 && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Fortschritt</span>
            <span className="text-sm font-medium text-gray-900">
              {completedSteps} von {planningSteps.length} abgeschlossen
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {Math.round(progressPercentage)}% abgeschlossen
          </div>
        </div>
      )}

      {planningSteps.length === 0 ? (
        <div className="text-center py-8">
          <ClipboardDocumentListIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Keine Planungsschritte vorhanden</h4>
          <p className="text-gray-600 mb-4">
            Erstellen Sie einen strukturierten Workflow für Ihr Projekt.
          </p>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            Ersten Schritt hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {planningSteps
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map((step, index) => (
              <div
                key={step.id}
                className={`border rounded-lg p-4 transition-all duration-200 ${
                  step.isCompleted 
                    ? 'bg-green-50 border-green-200' 
                    : step.isRequired 
                      ? 'bg-yellow-50 border-yellow-200' 
                      : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {/* Step Number/Status */}
                    <div className="flex-shrink-0">
                      {step.isCompleted ? (
                        <CheckCircleIconSolid className="w-6 h-6 text-green-500" />
                      ) : step.isRequired ? (
                        <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
                          <span className="text-white text-xs font-medium">{index + 1}</span>
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                          <span className="text-white text-xs font-medium">{index + 1}</span>
                        </div>
                      )}
                    </div>

                    {/* Step Content */}
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h4 className={`font-medium ${step.isCompleted ? 'text-green-800 line-through' : 'text-gray-900'}`}>
                          {step.stepName}
                        </h4>
                        {step.isRequired && (
                          <span className="ml-2 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                            Erforderlich
                          </span>
                        )}
                      </div>

                      {step.description && (
                        <p className={`text-sm mb-2 ${step.isCompleted ? 'text-green-700' : 'text-gray-600'}`}>
                          {step.description}
                        </p>
                      )}

                      {step.notes && (
                        <div className={`text-sm mb-2 p-2 rounded ${
                          step.isCompleted 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-50 text-blue-700'
                        }`}>
                          <span className="font-medium">Notizen:</span> {step.notes}
                        </div>
                      )}

                      {step.stepData && Object.keys(step.stepData).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                            Zusätzliche Daten anzeigen
                          </summary>
                          <pre className="text-xs text-gray-600 mt-1 bg-white rounded p-2 overflow-auto">
                            {JSON.stringify(step.stepData, null, 2)}
                          </pre>
                        </details>
                      )}

                      <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                        <span>Reihenfolge: {step.stepOrder}</span>
                        <span>Erstellt: {new Date(step.createdAt).toLocaleDateString('de-DE')}</span>
                        {step.completedAt && (
                          <span>Abgeschlossen: {new Date(step.completedAt).toLocaleDateString('de-DE')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleCompletion(step.id)}
                      className={`p-2 rounded-full transition-colors ${
                        step.isCompleted 
                          ? 'text-green-600 hover:bg-green-100' 
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={step.isCompleted ? 'Als unvollständig markieren' : 'Als abgeschlossen markieren'}
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleEditStep(step)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Bearbeiten"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => handleDeleteStep(step.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Löschen"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}

      {createModalOpen && (
        <CreatePlanningStepModal
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateStep}
          existingSteps={planningSteps}
        />
      )}

      {editModalOpen && selectedStep && (
        <EditPlanningStepModal
          step={selectedStep}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedStep(null);
          }}
          onSubmit={(updateData) => handleUpdateStep(selectedStep.id, updateData)}
          existingSteps={planningSteps.filter(s => s.id !== selectedStep.id)}
        />
      )}
    </div>
  );
};
